import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useCart, formatPrice } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { analytics } from "@/lib/analytics";
import { ArrowLeft, Minus, Plus, X } from "lucide-react";
import { useRouter } from "@tanstack/react-router";
import { toast } from "sonner";

export const Route = createFileRoute("/cart")({
  head: () => ({
    meta: [
      { title: "Your Bag — KAKAIYS" },
      { name: "description", content: "Review the pieces in your KAKAIYS bag." },
      { property: "og:title", content: "Your Bag — KAKAIYS" },
      { property: "og:description", content: "Review the pieces in your bag." },
    ],
  }),
  component: CartPage,
});

function CartPage() {
  const { items, setQty, remove, subtotal, count, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const router = useRouter();
  const handleBack = () => {
    if (typeof window !== "undefined" && window.history.length > 1) {
      router.history.back();
    } else {
      navigate({ to: "/shop" });
    }
  };

  const handleCheckout = () => {
    analytics.beginCheckout(
      items.map((i) => ({
        slug: i.product.slug,
        name: i.product.name,
        price: i.product.price,
        category: (i.product as { category?: string }).category,
        quantity: i.qty,
      })),
      subtotal,
    );
    if (!user) {
      toast.message("Please sign in to complete your purchase.");
      navigate({ to: "/auth", search: { redirect: "/cart" } });
      return;
    }
    toast.message("Checkout coming soon — payments not enabled in this preview.");
  };

  return (
    <section className="pt-32 md:pt-40 pb-24 container-luxe">
      <button
        type="button"
        onClick={handleBack}
        className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-muted-foreground hover:text-foreground transition-colors mb-8"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back
      </button>
      <div className="text-center mb-16">
        <div className="eyebrow mb-3">Your Selection</div>
        <h1 className="font-serif text-5xl md:text-7xl">The Bag.</h1>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-muted-foreground">Your bag is empty.</p>
          <Link
            to="/shop"
            className="mt-8 inline-flex text-xs uppercase tracking-[0.24em] border-b border-foreground pb-1"
          >
            Discover the Collection
          </Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-12 lg:gap-20">
          <div className="lg:col-span-2">
            <div className="hairline" />
            {items.map(({ product, qty }) => (
              <div
                key={product.slug}
                className="grid grid-cols-[100px_1fr_auto] md:grid-cols-[140px_1fr_auto] gap-4 md:gap-8 py-8 border-b border-border items-start"
              >
                <Link to="/product/$slug" params={{ slug: product.slug }}>
                  <div className="aspect-[4/5] bg-secondary overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      loading="lazy"
                      width={1024}
                      height={1280}
                      className="h-full w-full object-cover"
                    />
                  </div>
                </Link>
                <div>
                  <div className="eyebrow mb-2">{product.category}</div>
                  <Link
                    to="/product/$slug"
                    params={{ slug: product.slug }}
                    className="font-serif text-xl md:text-2xl underline-grow"
                  >
                    {product.name}
                  </Link>
                  <p className="text-xs text-muted-foreground mt-2">{product.tagline}</p>

                  <div className="mt-5 flex items-center gap-4">
                    <div className="flex items-center border border-border">
                      <button
                        className="p-2 hover:bg-secondary"
                        onClick={() => setQty(product.slug, qty - 1)}
                        aria-label="Decrease"
                      >
                        <Minus className="h-3 w-3" />
                      </button>
                      <span className="w-8 text-center text-sm tabular-nums">{qty}</span>
                      <button
                        className="p-2 hover:bg-secondary"
                        onClick={() => setQty(product.slug, qty + 1)}
                        aria-label="Increase"
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                    <button
                      onClick={() => remove(product.slug)}
                      className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                    >
                      <X className="h-3 w-3" /> Remove
                    </button>
                  </div>
                </div>
                <div className="text-sm tabular-nums whitespace-nowrap">
                  {formatPrice(product.price * qty)}
                </div>
              </div>
            ))}

            <button
              onClick={clear}
              className="mt-8 text-[11px] uppercase tracking-[0.24em] text-muted-foreground hover:text-foreground"
            >
              Clear bag
            </button>
          </div>

          {/* Summary */}
          <aside className="lg:sticky lg:top-28 self-start bg-secondary p-8 md:p-10">
            <div className="eyebrow mb-4">Order Summary</div>
            <h2 className="font-serif text-3xl">{count} {count === 1 ? "piece" : "pieces"}</h2>

            <div className="hairline my-6" />

            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="tabular-nums">{formatPrice(subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Shipping</span>
                <span>Complimentary</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Duties & taxes</span>
                <span>Calculated at checkout</span>
              </div>
            </div>

            <div className="hairline my-6" />

            <div className="flex justify-between font-serif text-2xl">
              <span>Total</span>
              <span className="tabular-nums">{formatPrice(subtotal)}</span>
            </div>

            <button
              type="button"
              onClick={handleCheckout}
              className="btn-luxe mt-8 w-full bg-foreground text-background py-4 text-[11px] uppercase tracking-[0.28em]"
            >
              {user ? "Proceed to Checkout" : "Sign in to Checkout"}
            </button>

            <p className="mt-4 text-center text-[11px] uppercase tracking-[0.24em] text-muted-foreground">
              Secure · Encrypted · Worldwide
            </p>
          </aside>
        </div>
      )}
    </section>
  );
}
